import type { Joi } from '@ksyos/koi';
import { DateRange, DateTime } from '@ksyos/date-time';
import { Koi } from '@ksyos/koi';
import { ServiceType } from '@common/enums';
import type { ISchemaContainer } from './schemaContainer';
import { positiveInt32Schema } from './schemas/positiveInt32Schema';

export interface IContractSettingAddEditValidated {
    contractPrice: number;
    endDate: string | Date | null;
    episodeCategoryId: string;
    id?: string;
    organizationId: string;
    serviceType: ServiceType;
    showDeleteButton?: boolean;
    startDate: string | Date;
}

const contractSettingAddEdit = Koi.object({
    contractPrice: positiveInt32Schema.required(),
    episodeCategoryId: Koi.string().uuid().required(),
    id: Koi.string().uuid().optional(),
    organizationId: Koi.string().uuid().required(),
    serviceType: Koi.koi().enum(ServiceType).required(),
    startDate: Koi.koi().date().required(),
}).options({ convert: false, presence: 'required', stripUnknown: true });

const contractSettingsForAllServiceTypes: Record<ServiceType, typeof contractSettingAddEdit> = Object.values(
    ServiceType
).reduce(
    (prev, curr) => ({
        ...prev,
        [curr]: Koi.array()
            .items(
                contractSettingAddEdit.concat(
                    Koi.object({
                        endDate: Koi.when('.', {
                            is: Koi.valid(null),
                            otherwise: Koi.koi()
                                .date()
                                .endDate()
                                .custom((value, helpers) => {
                                    return hasOverlapingDates(value, helpers) ? helpers.error('dateOverlap') : value;
                                }),
                            then: Koi.custom((value, helpers) => {
                                return hasOverlapingDates(value, helpers) ? helpers.error('dateOverlap') : value;
                            }),
                        }),
                    })
                )
            )
            .optional(),
    }),
    {} as Record<ServiceType, typeof contractSettingAddEdit>
);

export const contractSettingAddEditFrontendSchema: ISchemaContainer<
    Record<ServiceType, IContractSettingAddEditValidated[]>
> = {
    schema: Koi.object(contractSettingsForAllServiceTypes),
};

export const contractSettingsAddEditBackendSchema: ISchemaContainer<IContractSettingAddEditValidated[]> = {
    schema: Koi.array().items(
        contractSettingAddEdit.concat(
            Koi.object({
                endDate: Koi.koi().date().endDate().allow(null),
            })
        )
    ),
};

function hasOverlapingDates(endDate: Date | null, helpers: Joi.CustomHelpers) {
    const index = helpers.state.path && helpers.state.path[1];
    const startDate = helpers.state.ancestors[0].startDate;

    const dateRangeToValidate = new DateRange(DateTime.from(startDate), endDate ? DateTime.from(endDate) : null);

    const otherDateRanges = (helpers.state.ancestors[1] as IContractSettingAddEditValidated[])
        .filter((_, i: number) => i !== index)
        .map(
            (dateRange) =>
                new DateRange(
                    DateTime.from(dateRange.startDate),
                    dateRange.endDate ? DateTime.from(dateRange.endDate) : null
                )
        );

    return otherDateRanges.some((otherRange: DateRange) => dateRangeToValidate.hasOverlapWith(otherRange));
}
